import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send } from "lucide-react";
import { easeOutExpo, fadeUp } from "@/lib/motion";
import { API, ADDRESS, EMAIL, HOURS, PHONE, PHONE_LINK, waLink, DEFAULT_WA_MSG } from "@/lib/site";

const OCCASION_OPTIONS = ["Birthday", "Anniversary", "Festive & Diwali", "Wedding & Favors", "Corporate / Bulk", "Just browsing"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", occasion: OCCASION_OPTIONS[0], date: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, kind: "general" }),
      });
      if (!res.ok) throw new Error("bad status");
      toast.success("Thank you! Your gift request is with our studio — we'll call you soon.");
      setForm({ name: "", phone: "", occasion: OCCASION_OPTIONS[0], date: "", message: "" });
    } catch {
      toast.error("Couldn't reach the studio — WhatsApp us instead, we reply fast!");
    } finally {
      setBusy(false);
    }
  };

  const INFO = [
    { icon: Phone, label: "Call / WhatsApp", value: PHONE, href: PHONE_LINK, tid: "contact-phone" },
    { icon: Mail, label: "Email", value: EMAIL, href: `mailto:${EMAIL}`, tid: "contact-email" },
    { icon: MapPin, label: "Visit the atelier", value: ADDRESS, tid: "contact-address" },
    { icon: Clock, label: "Hours", value: HOURS, tid: "contact-hours" },
  ];

  return (
    <section id="contact" className="py-16 md:py-24 lg:py-32 bg-noir text-ivory relative overflow-hidden scroll-mt-16" data-testid="contact-section">
      <div className="pointer-events-none absolute -bottom-40 -left-32 h-[520px] w-[520px] rounded-full bg-terracotta/15 blur-[150px]" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative grid lg:grid-cols-2 gap-14">
        <motion.div {...fadeUp}>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold-light" data-testid="contact-eyebrow">
            Say Hello
          </p>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" data-testid="contact-heading">
            Let's gift-wrap <span className="italic text-gold-light">your story</span>
          </h2>
          <p className="mt-5 max-w-md text-sm sm:text-base leading-relaxed text-ivory/65" data-testid="contact-sub">
            Tell us the occasion and we'll suggest designs, share photos of
            past hampers and quote in minutes.
          </p>

          <ul className="mt-10 space-y-6">
            {INFO.map(({ icon: Icon, label, value, href, tid }, i) => (
              <motion.li key={label} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.07 * i }} className="flex items-start gap-4" data-testid={tid}>
                <span className="h-11 w-11 shrink-0 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center">
                  <Icon className="h-4.5 w-4.5 h-5 w-5 text-gold-light" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ivory/50">{label}</p>
                  {href ? (
                    <a href={href} className="mt-1 block text-[15px] font-semibold text-ivory hover:text-gold-light transition-colors duration-300" data-testid={`${tid}-link`}>
                      {value}
                    </a>
                  ) : (
                    <p className="mt-1 text-[15px] font-semibold text-ivory">{value}</p>
                  )}
                </div>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        <motion.div {...fadeUp} className="rounded-3xl bg-noir/60 border border-gold/25 p-6 sm:p-10 self-start" data-testid="contact-form-card">
          <h3 className="font-display text-3xl font-semibold" data-testid="contact-form-heading">Gift request</h3>
          <form onSubmit={submit} className="mt-7 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Your name</label>
                <input
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Asha Sharma"
                  className="mt-1.5 w-full rounded-xl bg-noir/50 border border-ivory/20 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/35 outline-none focus:border-gold transition-colors duration-300"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Phone</label>
                <input
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+91 …"
                  className="mt-1.5 w-full rounded-xl bg-noir/50 border border-ivory/20 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/35 outline-none focus:border-gold transition-colors duration-300"
                  data-testid="contact-phone-input"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Occasion</label>
                <select
                  value={form.occasion}
                  onChange={set("occasion")}
                  className="mt-1.5 w-full rounded-xl bg-noir/50 border border-ivory/20 px-4 py-3.5 text-sm text-ivory outline-none focus:border-gold transition-colors duration-300"
                  data-testid="contact-occasion-select"
                >
                  {OCCASION_OPTIONS.map((o) => (
                    <option key={o} value={o} className="text-charcoal">{o}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Needed by</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={set("date")}
                  className="mt-1.5 w-full rounded-xl bg-noir/50 border border-ivory/20 px-4 py-3.5 text-sm text-ivory outline-none focus:border-gold transition-colors duration-300"
                  data-testid="contact-date-input"
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Tell us your idea</label>
              <textarea
                rows="4"
                value={form.message}
                onChange={set("message")}
                placeholder="Budget, colors, the person you're gifting — anything helps…"
                className="mt-1.5 w-full rounded-xl bg-noir/50 border border-ivory/20 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/35 outline-none focus:border-gold transition-colors duration-300 resize-none"
                data-testid="contact-message-input"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <button
                type="submit"
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-crimson text-ivory font-semibold text-sm px-6 py-4 hover:bg-crimson-dark transition-colors duration-300 disabled:opacity-60"
                data-testid="contact-submit-button"
              >
                <Send className="h-4 w-4" />
                {busy ? "Sending…" : "Send request"}
              </button>
              <a
                href={waLink(DEFAULT_WA_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 text-gold-light font-semibold text-sm px-6 py-4 hover:bg-gold/10 transition-colors duration-300"
                data-testid="contact-whatsapp-button"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp instead
              </a>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
